// RC2 Sprint 0 · Reachability — Punch Center
// Two surfaces:
//   PunchCenter  (default)  → /punch
//   PunchCenter  (project)  → /project/:projectId/punch
// Reuses existing GET /projects/{pid}/punch-items endpoint (Iter 82.2).
// NO new functionality. Reachability + print only.
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate, NavLink } from "react-router-dom";
import {
  ClipboardList, Filter, Printer, ChevronRight, AlertTriangle,
  CheckCircle2, Clock, Circle, ExternalLink, Plus, X,
} from "lucide-react";
import { api } from "@/lib/api";
import { enqueue as offlineEnqueue } from "@/lib/offlineQueueV2";

const STATUS_FILTERS = [
  { key: "all",                 label: "All" },
  { key: "open",                label: "Open" },
  { key: "in_progress",         label: "In Progress" },
  { key: "ready_for_review",    label: "Ready" },
  { key: "closed",              label: "Closed" },
  { key: "rejected",            label: "Rejected" },
];

const STATUS_ICON = {
  open: Circle,
  in_progress: Clock,
  ready_for_review: AlertTriangle,
  closed: CheckCircle2,
  rejected: CheckCircle2,
};

const STATUS_COLOR = {
  open: "text-[var(--fop-text-2)]",
  in_progress: "text-orange-400",
  ready_for_review: "text-yellow-400",
  closed: "text-emerald-500",
  rejected: "text-red-500",
};

const PRIORITY_COLOR = {
  urgent: "bg-red-500/20 text-red-400 border-red-500/40",
  high:   "bg-orange-500/20 text-orange-300 border-orange-500/40",
  normal: "bg-[var(--fop-surface-2)] text-[var(--fop-text-2)] border-[var(--fop-border)]",
  low:    "bg-[var(--fop-surface-2)] text-[var(--fop-text-3)] border-[var(--fop-border)]",
};

export default function PunchCenter({ scope = "global" } = {}) {
  const params = useParams();
  const projectId = params.projectId || null;
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Pull projects (so the global view can iterate them).
  useEffect(() => {
    if (scope !== "global") return;
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data.projects || data || []);
      } catch { setProjects([]); }
    })();
  }, [scope]);

  const fetchScoped = useCallback(async (pid) => {
    try {
      const { data } = await api.get(`/projects/${pid}/punch-items`,
                                       { params: { limit: 200 } });
      return (data.items || data.punch_items || []).map((p) =>
        ({ ...p, _project_id: pid }));
    } catch { return []; }
  }, []);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        if (scope === "project" && projectId) {
          const proj = await api.get(`/projects/${projectId}`).then(
            (r) => r.data).catch(() => null);
          setProject(proj);
          const rows = await fetchScoped(projectId);
          setItems(rows);
        } else if (scope === "global") {
          if (projects.length === 0) { setItems([]); return; }
          const all = await Promise.all(
            projects.slice(0, 25).map((p) => fetchScoped(p.id)));
          setItems(all.flat());
        }
      } finally { setLoading(false); }
    })();
  }, [scope, projectId, projects, fetchScoped, reloadKey]);

  const visible = useMemo(() => {
    if (statusFilter === "all") return items;
    return items.filter((p) => p.status === statusFilter);
  }, [items, statusFilter]);

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  return (
    <div className="min-h-screen bg-[var(--fop-bg,#0a0a0b)] text-[var(--fop-text)]" data-testid="punch-center">
      <style>{`@media print {
        body { background: white !important; color: black !important; }
        [data-no-print] { display: none !important; }
        [data-testid="punch-center"] { background: white !important; color: black !important; }
        .print-light { background: white !important; color: black !important; border-color: #ccc !important; }
      }`}</style>
      <div className="max-w-6xl mx-auto px-6 py-8 print-light">
        <header className="mb-6 flex items-start justify-between gap-4" data-no-print>
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">
              {scope === "project" ? "Project · Punch" : "ForgedOps · Punch Center"}
            </div>
            <h1 className="text-2xl font-semibold mt-1">
              {scope === "project"
                ? (project?.name || "Punch list")
                : "All open punch items"}
            </h1>
            <div className="text-[12px] text-[var(--fop-text-3)] mt-1">
              {visible.length} item{visible.length === 1 ? "" : "s"} · {statusFilter.replace("_", " ")}
            </div>
          </div>
          <button
            onClick={() => window.print()}
            data-testid="punch-print-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded border border-[var(--fop-primary)]/40 text-[12px] font-mono uppercase tracking-wider text-[var(--fop-primary)] hover:bg-[var(--fop-primary)]/10 min-h-[44px]"
          >
            <Printer size={14} /> Print list
          </button>
          {scope === "project" && (
            <button
              onClick={() => setShowNew(true)}
              data-testid="punch-new-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded bg-[var(--fop-primary)] text-black font-mono uppercase tracking-wider text-[12px] hover:bg-[var(--fop-primary)]/90 min-h-[44px]"
            >
              <Plus size={14} /> New Punch
            </button>
          )}
        </header>

        <div className="flex gap-1 mb-4 flex-wrap" data-no-print>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              data-testid={`punch-filter-${f.key}`}
              className={`text-[11px] uppercase tracking-wider font-mono
                            px-2.5 py-1 rounded transition-colors
                            ${statusFilter === f.key
                              ? "bg-[var(--fop-primary)] text-black"
                              : "text-[var(--fop-text-3)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface)]"}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Print header */}
        <div className="hidden print:block mb-4 pb-2 border-b border-gray-400">
          <h2 className="text-lg font-semibold text-black">
            Punch List — {scope === "project" ? (project?.name || "") : "All projects"}
          </h2>
          <div className="text-xs text-gray-700">Generated {new Date().toISOString().slice(0,19).replace("T", " ")}</div>
        </div>

        <div className="border border-[var(--fop-border)] rounded-md bg-[var(--fop-surface)] overflow-hidden print-light">
          {loading && (
            <div className="px-4 py-8 text-center text-[var(--fop-text-3)] text-sm" data-testid="punch-loading">Loading…</div>
          )}
          {!loading && visible.length === 0 && (
            <div className="px-6 py-12 text-center" data-testid="punch-empty">
              <CheckCircle2 size={28} className="mx-auto text-emerald-500 mb-2" />
              <div className="text-sm text-[var(--fop-text-2)]">No punch items here.</div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--fop-border)] text-[10px] uppercase tracking-wider font-mono text-[var(--fop-text-3)]">
                <th className="text-left px-4 py-2 w-8" />
                <th className="text-left px-2 py-2">Title</th>
                {scope === "global" && <th className="text-left px-2 py-2">Project</th>}
                <th className="text-left px-2 py-2">Status</th>
                <th className="text-left px-2 py-2">Priority</th>
                <th className="text-left px-2 py-2">Due</th>
                <th className="text-left px-2 py-2" data-no-print></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => {
                const Icon = STATUS_ICON[p.status] || Circle;
                return (
                  <tr
                    key={p.id}
                    onClick={() => navigate(`/project/${p._project_id || p.project_id}/punch/${p.id}`)}
                    className="border-b border-[var(--fop-border)] last:border-b-0 hover:bg-[var(--fop-surface-2)] cursor-pointer transition-colors"
                    data-testid={`punch-row-${p.id}`}
                  >
                    <td className="px-4 py-2">
                      <Icon size={14} className={STATUS_COLOR[p.status] || "text-[var(--fop-text-2)]"} />
                    </td>
                    <td className="px-2 py-2 truncate max-w-[40ch]">{p.title}</td>
                    {scope === "global" && (
                      <td className="px-2 py-2 text-[12px] text-[var(--fop-text-2)]">
                        {projectName(p._project_id || p.project_id)}
                      </td>
                    )}
                    <td className="px-2 py-2 text-[12px] font-mono uppercase tracking-wider text-[var(--fop-text-2)]">
                      {(p.status || "").replace("_", " ")}
                    </td>
                    <td className="px-2 py-2">
                      <span className={`inline-block text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border ${PRIORITY_COLOR[p.priority] || PRIORITY_COLOR.normal}`}>
                        {p.priority || "normal"}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-[12px] text-[var(--fop-text-2)]">
                      {p.due_date ? String(p.due_date).slice(0, 10) : "—"}
                    </td>
                    <td className="px-2 py-2 text-right" data-no-print>
                      <ChevronRight size={14} className="text-[var(--fop-text-3)]" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {showNew && (
        <NewPunchModal
          projectId={projectId}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); setReloadKey((k) => k + 1); }}
        />
      )}
    </div>
  );
}

function NewPunchModal({ projectId, onClose, onCreated }) {
  const [title, setTitle] = React.useState("");
  const [priority, setPriority] = React.useState("normal");
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const submit = async () => {
    if (!title.trim()) { setErr("Title is required"); return; }
    setSubmitting(true); setErr(null);
    const body = { title: title.trim(), priority, status: "open" };
    // Iter 85 Phase 7 — Offline punch engine. When offline, the
    // mutation is enqueued + flushed on reconnect. No silent failure.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      try {
        offlineEnqueue("punch.create", { projectId, body });
        onCreated();
        return;
      } catch (e) {
        setErr("Could not queue punch offline.");
        setSubmitting(false);
        return;
      }
    }
    try {
      await api.post(`/projects/${projectId}/punch-items`, body);
      onCreated();
    } catch (e) {
      // Network failure mid-request → fall through to offline queue.
      if (!e?.response) {
        try {
          offlineEnqueue("punch.create", { projectId, body });
          onCreated();
          return;
        } catch { /* ignore */ }
      }
      setErr(e?.response?.data?.detail || "Could not create punch.");
    } finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center px-4" onClick={onClose} data-testid="punch-new-modal">
      <div
        className="bg-[var(--fop-surface)] border border-[var(--fop-primary)] rounded-lg shadow-2xl p-5 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--fop-primary)]">New Punch Item</div>
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" data-testid="punch-new-close">
            <X size={16} />
          </button>
        </div>
        <label className="block text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Title</label>
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") onClose(); }}
          placeholder="e.g. Pier 3 footing rebar spacing"
          className="w-full text-sm py-2 px-3 bg-[var(--fop-bg)] border border-[var(--fop-border)] rounded mb-3 min-h-[44px]"
          data-testid="punch-new-title"
        />
        <label className="block text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Priority</label>
        <select
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          className="w-full text-sm py-2 px-3 bg-[var(--fop-bg)] border border-[var(--fop-border)] rounded mb-4 min-h-[44px]"
          data-testid="punch-new-priority"
        >
          <option value="low">Low</option>
          <option value="normal">Normal</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
        {err && <div className="text-red-400 text-[12px] mb-3" data-testid="punch-new-err">{err}</div>}
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 text-[12px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)] min-h-[44px]"
            data-testid="punch-new-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="px-3 py-2 rounded bg-[var(--fop-primary)] text-black font-mono uppercase tracking-wider text-[12px] hover:bg-[var(--fop-primary)]/90 disabled:opacity-50 min-h-[44px]"
            data-testid="punch-new-save"
          >
            {submitting ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
